import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { MediaAsset } from 'src/models/FundraisingModel';
import DeleteIcon from '@material-ui/icons/Delete';
import VideocamIcon from '@material-ui/icons/Videocam';
import { toKebabCase } from 'src/lib/FieldHelper';
import { useAppDispatch } from 'src/redux/Store';
import { DIALOGS } from 'src/models/DialogModel';
import { openDialog } from 'src/redux/actions/dialogActions';
import { Tooltip } from '@material-ui/core';
import Row from '../UI/Row';

interface Props {
  onChange: (assetList: MediaAsset[]) => void,
  ratio: number,
  assetList: MediaAsset[],
}

const MainFundraisingSlider = ({
  onChange, ratio, assetList,
}: Props) => {
  const dispatch = useAppDispatch();

  const HEIGHT = 140;
  const getWidth = (height: number) => `${height * ratio}px`;

  const onDelete = (index: number) => {
    const newList = assetList.filter((_, current) => current !== index);
    onChange(newList);
  };

  const onVideoUrlChange = (asset: MediaAsset) => {
    const assets = assetList.map(
      (current) => (current.image_url === asset.image_url
        ? { ...current, video_url: asset.video_url }
        : current),
    );
    onChange(assets);
  };

  const onClickEditVideo = (asset: MediaAsset) => {
    dispatch(
      openDialog({ dialog: DIALOGS.editVideoUrl, payload: { asset, onChange: onVideoUrlChange } }),
    );
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    const newList = Array.from(assetList);
    const [removed] = newList.splice(source.index, 1);
    newList.splice(destination.index, 0, removed);

    onChange(newList);
  };

  const getTooltip = (asset: MediaAsset) => {
    if (!asset.video_url) return 'Video URL not set';
    return `Video URL: ${asset.video_url}`;
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable" direction="horizontal">
        {(provided: any) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{
              width: '100%',
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              height: `${HEIGHT}px`,
            }}>
            {assetList.map((asset, index) => (
              <Draggable
                key={toKebabCase(asset.image_url)}
                draggableId={toKebabCase(asset.image_url)}
                index={index}>
                {(providedDraggable: any) => (
                  <div
                    ref={providedDraggable.innerRef}
                    {...providedDraggable.draggableProps}
                    {...providedDraggable.dragHandleProps}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexShrink: 0,
                      flexGrow: 0,
                      border: '1px solid #c8c8c8',
                      borderRadius: '8px',
                      width: getWidth(HEIGHT),
                      height: `${HEIGHT}px`,
                      backgroundImage: `url(${asset.image_url}?${performance.now()})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      ...providedDraggable.draggableProps.style,
                    }}>
                    <Row style={{ justifyContent: 'end', paddingRight: '8px' }} alignItems="center">
                      <div
                        style={{
                          width: 'fit-content',
                          height: '24px',
                          background: 'rgba(255, 255, 255, 0.75)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        onClick={() => onDelete(index)}>
                        <DeleteIcon />
                      </div>
                      <Tooltip
                        title={getTooltip(asset)}
                        placement="top-start">
                        <div
                          onClick={() => onClickEditVideo(asset)}
                          style={{
                            width: 'fit-content',
                            height: '24px',
                            background: 'rgba(255, 255, 255, 0.75)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}>
                          <VideocamIcon />
                        </div>
                      </Tooltip>
                    </Row>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default MainFundraisingSlider;
